import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useComplianceUsers, useUpdateAuditAssignee } from "@/hooks/use-audits";

interface EditAuditAssigneeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  auditId: string;
  currentAssigneeId: string;
}

export function EditAuditAssigneeDialog({
  open,
  onOpenChange,
  auditId,
  currentAssigneeId,
}: EditAuditAssigneeDialogProps) {
  const { data: complianceUsers } = useComplianceUsers();
  const updateAssignee = useUpdateAuditAssignee(auditId);

  const auditors = (complianceUsers ?? []).filter(
    (u) => u.roles?.includes("compliance_auditor") || u.role === "compliance_auditor",
  );

  const [selectedId, setSelectedId] = useState(currentAssigneeId);

  // Read via a ref (not a dependency) so a background refetch of
  // currentAssigneeId while the dialog is already open can't stomp the
  // user's in-progress selection — the reset should only happen when the
  // dialog transitions to open.
  const currentAssigneeIdRef = useRef(currentAssigneeId);
  currentAssigneeIdRef.current = currentAssigneeId;

  useEffect(() => {
    if (open) {
      setSelectedId(currentAssigneeIdRef.current);
    }
  }, [open]);

  function handleSave() {
    if (!selectedId || selectedId === currentAssigneeId) {
      onOpenChange(false);
      return;
    }
    updateAssignee.mutate({ assignedToId: selectedId }, { onSuccess: () => onOpenChange(false) });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Reassign Compliance Auditor</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <Select onValueChange={setSelectedId} value={selectedId}>
            <SelectTrigger>
              <SelectValue placeholder="Select an auditor" />
            </SelectTrigger>
            <SelectContent>
              {auditors.map((u) => (
                <SelectItem key={u.id} value={u.id}>
                  {u.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleSave}
              disabled={updateAssignee.isPending || !selectedId}
            >
              {updateAssignee.isPending ? "Saving…" : "Save"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
